import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getStoredMagicLinkEmail, verifyMagicLinkSignIn } from '../../services/AuthService';

const classifyFirebaseError = (e) => {
  const code = e?.code || '';
  if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code')
    return 'used';
  if (code === 'auth/network-request-failed')
    return 'network';
  return 'generic';
};

const translateFirebaseError = (e) => {
  const code = e?.code || '';
  if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code')
    return 'Este link de acesso já foi utilizado ou expirou.';
  if (code === 'auth/invalid-email')
    return 'O e-mail não corresponde ao autorizado para este link. Solicite um novo link ao escritório.';
  if (code === 'auth/user-disabled')
    return 'Esta conta foi suspensa temporariamente. Entre em contato com o escritório.';
  if (code === 'auth/network-request-failed')
    return 'Sem conexão com a internet. Verifique sua rede e tente novamente.';
  return 'Link inválido ou expirado. Solicite um novo link ao escritório.';
};

export default function AuthCallback({ onDone }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('generic');
  const [emailInput, setEmailInput] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const hrefRef = useRef(window.location.href);

  const finalize = (result) => {
    const params = new URLSearchParams(window.location.search);
    const nextParam = params.get('next');
    const clientId = params.get('clientId');

    if (clientId) {
      try { window.sessionStorage.setItem('portalClientId', clientId); } catch {}
    }

    if (nextParam && nextParam.startsWith('/portal/')) {
      navigate(nextParam, { replace: true });
    } else if (typeof onDone === 'function') {
      onDone(clientId || null);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const result = await verifyMagicLinkSignIn({
          href: hrefRef.current,
          fallbackEmail: getStoredMagicLinkEmail(),
        });

        if (!result?.ok && result?.reason === 'invalid-link') {
          setStatus('error');
          setError('Link inválido ou expirado. Solicite um novo link ao escritório.');
          return;
        }

        if (!result?.ok && result?.reason === 'missing-email') {
          setStatus('needs-email');
          return;
        }

        if (!result?.ok) {
          setStatus('error');
          setError('Não foi possível validar o link de acesso. Solicite um novo link ao escritório.');
          return;
        }

        setStatus('ok');
        finalize(result);
      } catch (e) {
        setErrorType(classifyFirebaseError(e));
        setStatus('error');
        setError(translateFirebaseError(e));
      }
    };

    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    setEmailLoading(true);
    try {
      const result = await verifyMagicLinkSignIn({
        href: hrefRef.current,
        fallbackEmail: email,
      });
      if (result?.ok) {
        setStatus('ok');
        finalize(result);
      } else {
        setErrorType(classifyFirebaseError({}));
        setStatus('error');
        setError('E-mail não corresponde ao link. Verifique e tente novamente.');
      }
    } catch (e) {
      setErrorType(classifyFirebaseError(e));
      setStatus('error');
      setError(translateFirebaseError(e));
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-md p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-[#20243B]"></div>
            <div className="text-sm font-extrabold text-[#20243B] uppercase tracking-widest">Autenticando</div>
            <div className="mt-2 text-xs text-gray-600">Validando seu acesso seguro...</div>
          </>
        )}

        {status === 'needs-email' && (
          <>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 mx-auto mb-4">
              <svg className="w-6 h-6 text-[#20243B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </div>
            <div className="text-sm font-extrabold text-[#20243B] uppercase tracking-widest mb-1">Confirme seu e-mail</div>
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              Para concluir o acesso, informe o e-mail onde você recebeu este link.
            </p>
            <form onSubmit={handleEmailSubmit} className="text-left">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="seu@email.com"
                required
                autoFocus
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-gray-700 mb-3 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
              />
              <button
                type="submit"
                disabled={emailLoading}
                className="w-full py-2.5 text-xs font-extrabold uppercase tracking-widest text-white bg-[#20243B] rounded-xl hover:bg-[#1a1e30] transition-all disabled:opacity-60"
              >
                {emailLoading ? 'Verificando...' : 'Confirmar Acesso'}
              </button>
            </form>
          </>
        )}

        {status === 'error' && errorType === 'used' && (
          <>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="text-sm font-extrabold text-[#20243B] uppercase tracking-widest mb-2">Link já utilizado</div>
            <div className="text-xs text-gray-600 leading-relaxed mb-1">
              {error}
            </div>
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left">
              <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wide mb-1">Por que isso acontece?</p>
              <p className="text-[11px] text-amber-700 leading-relaxed">
                Por segurança, cada link de acesso funciona <strong>uma única vez</strong>. Após o primeiro clique, ele é invalidado automaticamente — mesmo que você não tenha conseguido entrar.
              </p>
            </div>
            <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
              Para acessar o portal novamente, solicite um novo link ao escritório.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 w-full py-2.5 text-xs font-extrabold uppercase tracking-widest text-white bg-[#20243B] rounded-xl hover:bg-[#1a1e30] transition-all"
            >
              Voltar para o Início
            </button>
          </>
        )}

        {status === 'error' && errorType !== 'used' && (
          <>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div className="text-sm font-bold text-red-700 mb-1">Não foi possível autenticar</div>
            <div className="text-xs text-gray-500 leading-relaxed mb-4">{error}</div>
            <button
              onClick={() => navigate('/')}
              className="w-full py-2.5 text-xs font-extrabold uppercase tracking-widest text-white bg-[#20243B] rounded-xl hover:bg-[#1a1e30] transition-all"
            >
              Voltar para o Início
            </button>
          </>
        )}
      </div>
    </div>
  );
}
